import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { 
  FileSpreadsheet, 
  FileText, 
  Download, 
  Search, 
  ArrowUp, 
  ArrowDown, 
  UserCog,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import * as XLSX from 'xlsx';
import * as Papa from 'papaparse';
import { saveAs } from 'file-saver';

type SortDirection = 'asc' | 'desc';

interface SortConfig {
  key: string;
  direction: SortDirection;
}

interface RefereeAssignment {
  assignment_id: string;
  event_id: string;
  referee_id: string;
  role: string;
  created_at: string;
  event: {
    event_id: string;
    event_name: string;
    reference_number: string;
    status: string;
    category: {
      category_id: string;
      category_name: string;
    };
    age_group?: {
      group_name: string;
    };
  };
  referee: {
    referee_id: string;
    first_name: string;
    last_name: string;
    first_name_tamil?: string;
    last_name_tamil?: string;
    email: string;
    phone?: string;
    department?: {
      name: string;
      name_tamil?: string;
    };
  };
}

interface GroupedAssignment {
  referee_id: string;
  first_name: string;
  last_name: string;
  first_name_tamil?: string;
  last_name_tamil?: string;
  email: string;
  phone?: string;
  department?: {
    name: string;
    name_tamil?: string;
  };
  assignments: RefereeAssignment[];
  expanded: boolean;
}

const RefereeAssignmentsReport: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [assignments, setAssignments] = useState<RefereeAssignment[]>([]);
  const [groupedAssignments, setGroupedAssignments] = useState<GroupedAssignment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [categories, setCategories] = useState<{id: string, name: string}[]>([]);
  const [roles, setRoles] = useState<string[]>(['head', 'assistant', 'timekeeper', 'scorer']);
  const [statuses, setStatuses] = useState<string[]>(['not_started', 'in_progress', 'completed']);
  
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedRole, setSelectedRole] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');

  useEffect(() => {
    checkAuthorization();
  }, []);

  useEffect(() => {
    if (assignments.length > 0) {
      groupAssignmentsByReferee();
    }
  }, [assignments, searchTerm, selectedCategory, selectedRole, selectedStatus]);

  useEffect(() => {
    if (isAuthorized) {
      // Set up real-time subscription for referee assignments
      const assignmentsSubscription = supabase
        .channel('referee_assignments_changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'event_referees' 
        }, () => {
          console.log('Referee assignments changed, refreshing data...');
          loadData();
        })
        .subscribe();
      
      // Clean up subscription on unmount
      return () => {
        assignmentsSubscription.unsubscribe();
      };
    }
  }, [isAuthorized]);

  const checkAuthorization = async () => {
    try {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      const hasAccess = userRoles?.some(ur => 
        ur.roles.role_name === 'admin' || 
        ur.roles.role_name === 'manager'
      ) ?? false;

      setIsAuthorized(hasAccess);
      if (!hasAccess) {
        navigate('/');
      } else {
        loadData();
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setError('Failed to verify access permissions');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all referee assignments with related data
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('event_referees')
        .select(`
          *,
          event:events(
            event_id,
            event_name,
            reference_number,
            status,
            category:sports_categories(
              category_id,
              category_name
            ),
            age_group:age_groups(
              age_group_id,
              group_name
            )
          ),
          referee:referees(
            referee_id,
            first_name,
            last_name,
            first_name_tamil,
            last_name_tamil,
            email,
            phone,
            department:departments(
              department_id,
              name,
              name_tamil
            )
          )
        `)
        .order('created_at', { ascending: false });
      
      if (assignmentsError) throw assignmentsError;
      
      setAssignments(assignmentsData || []);
      
      // Extract unique categories for filter
      const uniqueCategories = Array.from(new Set(
        assignmentsData?.filter(a => a.event?.category).map(a => ({
          id: a.event.category.category_id,
          name: a.event.category.category_name
        })) || []
      )).sort((a, b) => a.name.localeCompare(b.name));
      
      setCategories(uniqueCategories);
      setRefreshing(false);
      
    } catch (error: any) {
      console.error('Error loading data:', error);
      setError(error.message || 'Failed to load data');
      setRefreshing(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const groupAssignmentsByReferee = () => {
    const filteredAssignments = getFilteredAssignments();
    
    // Group by referee_id
    const groupedByReferee: { [key: string]: RefereeAssignment[] } = {};
    
    filteredAssignments.forEach(assignment => {
      const refereeId = assignment.referee_id;
      if (!groupedByReferee[refereeId]) {
        groupedByReferee[refereeId] = [];
      }
      groupedByReferee[refereeId].push(assignment);
    });
    
    // Convert to array of grouped assignments
    const grouped: GroupedAssignment[] = Object.entries(groupedByReferee).map(([refereeId, assignments]) => {
      const referee = assignments[0].referee;
      return {
        referee_id: refereeId,
        first_name: referee.first_name,
        last_name: referee.last_name,
        first_name_tamil: referee.first_name_tamil,
        last_name_tamil: referee.last_name_tamil,
        email: referee.email,
        phone: referee.phone,
        department: referee.department,
        assignments: assignments,
        expanded: false
      };
    });
    
    // Sort by referee name
    grouped.sort((a, b) => {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    setGroupedAssignments(grouped);
  };

  const getFilteredAssignments = () => {
    return assignments.filter(assignment => {
      const searchString = searchTerm.toLowerCase();
      const matchesSearch = (
        `${assignment.referee?.first_name || ''} ${assignment.referee?.last_name || ''}`.toLowerCase().includes(searchString) ||
        (assignment.referee?.first_name_tamil && assignment.referee?.last_name_tamil && 
          `${assignment.referee.first_name_tamil} ${assignment.referee.last_name_tamil}`.toLowerCase().includes(searchString)) ||
        (assignment.event?.event_name || '').toLowerCase().includes(searchString) ||
        (assignment.event?.reference_number || '').toLowerCase().includes(searchString) ||
        assignment.role.toLowerCase().includes(searchString) ||
        (assignment.referee?.department?.name || '').toLowerCase().includes(searchString)
      );
      
      const matchesCategory = selectedCategory === 'All' || assignment.event?.category?.category_id === selectedCategory;
      const matchesRole = selectedRole === 'All' || assignment.role === selectedRole;
      const matchesStatus = selectedStatus === 'All' || assignment.event?.status === selectedStatus;
      
      return matchesSearch && matchesCategory && matchesRole && matchesStatus;
    });
  };

  const toggleExpand = (refereeId: string) => {
    setGroupedAssignments(prev => 
      prev.map(group => 
        group.referee_id === refereeId 
          ? { ...group, expanded: !group.expanded } 
          : group
      )
    );
  };

  const requestSort = (key: string) => {
    let direction: SortDirection = 'asc';
    
    if (sortConfig && sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return null;
    }
    
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 inline-block ml-1" /> 
      : <ArrowDown className="h-4 w-4 inline-block ml-1" />;
  };

  const exportToExcel = () => {
    // Prepare data for export - flatten grouped assignments
    const data: any[] = [];
    
    groupedAssignments.forEach(group => {
      // Add a row for the referee
      data.push({
        'Referee': `${group.first_name} ${group.last_name}`,
        'Tamil Name': group.first_name_tamil && group.last_name_tamil ? 
          `${group.first_name_tamil} ${group.last_name_tamil}` : '',
        'Email': group.email,
        'Phone': group.phone || '',
        'Department': group.department?.name || '',
        'Total Assignments': group.assignments.length,
        'Competition': '',
        'Reference Number': '',
        'Category': '',
        'Age Group': '',
        'Role': '',
        'Status': ''
      });
      
      // Add rows for each assignment
      group.assignments.forEach(assignment => {
        data.push({
          'Referee': '',
          'Tamil Name': '',
          'Email': '',
          'Phone': '',
          'Department': '',
          'Total Assignments': '',
          'Competition': assignment.event.event_name,
          'Reference Number': assignment.event.reference_number,
          'Category': assignment.event.category.category_name,
          'Age Group': assignment.event.age_group?.group_name || '',
          'Role': getRoleTranslation(assignment.role),
          'Status': getStatusTranslation(assignment.event.status)
        });
      });
      
      // Add an empty row for spacing
      data.push({
        'Referee': '',
        'Tamil Name': '',
        'Email': '',
        'Phone': '',
        'Department': '',
        'Total Assignments': '',
        'Competition': '',
        'Reference Number': '',
        'Category': '',
        'Age Group': '',
        'Role': '',
        'Status': ''
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Referee Assignments");
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(dataBlob, 'referee_assignments_by_referee.xlsx');
  };

  const exportToCSV = () => {
    // Prepare data for export - flatten grouped assignments
    const data: any[] = [];
    
    groupedAssignments.forEach(group => {
      // Add a row for the referee
      data.push({
        'Referee': `${group.first_name} ${group.last_name}`,
        'Tamil Name': group.first_name_tamil && group.last_name_tamil ? 
          `${group.first_name_tamil} ${group.last_name_tamil}` : '',
        'Email': group.email,
        'Phone': group.phone || '',
        'Department': group.department?.name || '',
        'Total Assignments': group.assignments.length,
        'Competition': '',
        'Reference Number': '',
        'Category': '',
        'Age Group': '',
        'Role': '',
        'Status': ''
      });
      
      // Add rows for each assignment
      group.assignments.forEach(assignment => {
        data.push({
          'Referee': '',
          'Tamil Name': '',
          'Email': '',
          'Phone': '',
          'Department': '',
          'Total Assignments': '',
          'Competition': assignment.event.event_name,
          'Reference Number': assignment.event.reference_number,
          'Category': assignment.event.category.category_name,
          'Age Group': assignment.event.age_group?.group_name || '',
          'Role': getRoleTranslation(assignment.role),
          'Status': getStatusTranslation(assignment.event.status)
        });
      });
      
      // Add an empty row for spacing
      data.push({
        'Referee': '',
        'Tamil Name': '',
        'Email': '',
        'Phone': '',
        'Department': '',
        'Total Assignments': '',
        'Competition': '',
        'Reference Number': '',
        'Category': '',
        'Age Group': '',
        'Role': '',
        'Status': ''
      });
    });

    const csv = Papa.unparse(data);
    const csvBlob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(csvBlob, 'referee_assignments_by_referee.csv');
  };

  const getRoleTranslation = (role: string): string => {
    switch (role) {
      case 'head':
        return t('reports.refereeAssignments.roles.head');
      case 'assistant':
        return t('reports.refereeAssignments.roles.assistant');
      case 'timekeeper':
        return t('reports.refereeAssignments.roles.timekeeper');
      case 'scorer':
        return t('reports.refereeAssignments.roles.scorer');
      default:
        return role;
    }
  };

  const getStatusTranslation = (status: string): string => {
    return t(`competitions.status.${status}`);
  };

  const getRoleClass = (role: string): string => {
    switch (role) {
      case 'head':
        return 'bg-red-100 text-red-800';
      case 'assistant':
        return 'bg-blue-100 text-blue-800';
      case 'timekeeper':
        return 'bg-green-100 text-green-800';
      case 'scorer':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'not_started':
        return 'bg-gray-100 text-gray-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{t('reports.refereeAssignments.title')}</h1>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('reports.actions.export')}
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu">
                  <button
                    onClick={exportToExcel}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {t('reports.export.toExcel')}
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {t('reports.export.toCSV')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('reports.refereeAssignments.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            
            <div className="flex-shrink-0">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('reports.refereeAssignments.filters.allCategories')}</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex-shrink-0">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('reports.refereeAssignments.filters.allRoles')}</option>
                {roles.map(role => (
                  <option key={role} value={role}>
                    {getRoleTranslation(role)}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex-shrink-0">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('reports.refereeAssignments.filters.allStatuses')}</option>
                {statuses.map(status => (
                  <option key={status} value={status}>
                    {getStatusTranslation(status)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : groupedAssignments.length === 0 ? (
          <div className="text-center py-12">
            <UserCog className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('reports.refereeAssignments.noAssignments.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedCategory !== 'All' || selectedRole !== 'All' || selectedStatus !== 'All'
                ? t('reports.refereeAssignments.noAssignments.description.withFilters')
                : t('reports.refereeAssignments.noAssignments.description.noFilters')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {groupedAssignments.map((group) => (
              <div key={group.referee_id} className="bg-white">
                <div 
                  className="px-6 py-4 cursor-pointer hover:bg-gray-50 flex justify-between items-center"
                  onClick={() => toggleExpand(group.referee_id)}
                >
                  <div className="flex-1">
                    <div className="flex items-center">
                      {group.expanded ? 
                        <ChevronDown className="h-5 w-5 text-gray-400 mr-2" /> : 
                        <ChevronRight className="h-5 w-5 text-gray-400 mr-2" />
                      }
                      <div>
                        <div className="text-lg font-medium text-gray-900">
                          {group.first_name} {group.last_name}
                        </div>
                        {group.department?.name && (
                          <div className="text-sm text-gray-500">
                            {group.department.name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm font-medium bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                    {group.assignments.length} {group.assignments.length === 1 ? 'assignment' : 'assignments'}
                  </div>
                </div>
                
                {group.expanded && (
                  <div className="border-t border-gray-100 bg-gray-50">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.refereeAssignments.table.competition')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.refereeAssignments.table.category')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.refereeAssignments.table.role')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.refereeAssignments.table.status')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('reports.refereeAssignments.table.reference')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {group.assignments.map((assignment) => (
                          <tr key={assignment.assignment_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {assignment.event.event_name}
                              </div>
                              {assignment.event.age_group && (
                                <div className="text-xs text-gray-500">
                                  {assignment.event.age_group.group_name}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {assignment.event.category.category_name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleClass(assignment.role)}`}>
                                {getRoleTranslation(assignment.role)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(assignment.event.status)}`}>
                                {getStatusTranslation(assignment.event.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-mono text-gray-900">
                                {assignment.event.reference_number}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RefereeAssignmentsReport;